import cn from '@integrations/classnames'
import type { ElementType, HTMLAttributes } from '@integrations/react'
import styles from './PageShell.module.scss'

type PageShellProps = HTMLAttributes<HTMLDivElement>
type PageShellAlignment = 'start' | 'center'
type PageTitleVariant = 'default' | 'responsive'

interface PageShellComponentProps extends PageShellProps {
  align?: PageShellAlignment
  /** Desktop: the page is exactly one screen tall and its list card scrolls inside it (see `page-fill-card`). */
  fill?: boolean
}

interface PageTitleProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType
  variant?: PageTitleVariant
}
type PageSurfaceProps = HTMLAttributes<HTMLDivElement>

/**
 * Class names for the parts of a `fill` page (desktop only, the rules live in PageShell.module.scss): `item` for each
 * wrapper between the page and the card, `card` for the list card, `PageFillScroll` for the part of the card that scrolls, `rows` (on the scroll part) to give a paged table's rows all the same height, a tenth of the space left under the header.
 */
export const PAGE_FILL = {
  item: cn(styles['page-fill-item'], 'page-fill-item'),
  card: cn(styles['page-fill-card'], 'page-fill-card'),
  rows: cn(styles['page-fill-rows'], 'page-fill-rows'),
  // a row of a `rows` table that keeps its natural height (a nested row, for example)
  naturalRow: 'page-fill-natural-row'
}

export function PageShell({ align = 'start', fill, className, children, ...props }: PageShellComponentProps) {
  return (
    <div
      className={cn(styles['page-shell'], 'page-shell', className, {
        [styles['page-shell-centered']]: align === 'center',
        [styles['page-shell-fill']]: fill
      })}
      {...props}
    >
      {children}
    </div>
  )
}

export function PageTitle({ as: Element = 'h1', variant = 'default', className, children, ...props }: PageTitleProps) {
  return (
    <Element
      className={cn(styles['page-title'], 'page-title', className, {
        [styles['page-title-responsive']]: variant === 'responsive'
      })}
      {...props}
    >
      {children}
    </Element>
  )
}

/** Page heading row: the title, optional inline extras, and `PageActions` (pushed to the right; below 540px on their own row, left-aligned). */
export function PageHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['page-header'], 'page-header', className)} {...props}>
      {children}
    </div>
  )
}

/** Supporting sentence under a `PageTitle`; group both in a `div` inside `PageHeader`. */
export function PageSubtitle({ className, children, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn(styles['page-subtitle'], 'page-subtitle', className)} {...props}>
      {children}
    </p>
  )
}

/** Page-level action pills inside `PageHeader`: right-aligned beside the title, left-aligned on their own row below 540px. */
export function PageActions({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(styles['page-actions'], 'page-actions', className)} {...props}>
      {children}
    </div>
  )
}

export function PageSurface({ className, children, ...props }: PageSurfaceProps) {
  return (
    <div className={cn(styles['page-surface'], 'page-surface', className)} {...props}>
      {children}
    </div>
  )
}
